/** @jsxImportSource @emotion/react */
import { useFetchGlossaryTerms } from '@app/components/Guide/hooks/useFetchGlossaryTerms'
import { alphabet, groupGlossaryByLetter } from '@app/components/Guide/utils/groupGlossaryByLetter'
import { CenteredBox } from '@app/components/ui/CenteredBox/CenteredBox'
import { Box, CircularProgress, useTheme } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  glossaryContents,
  glossaryContentsOuter,
  letterIndex,
  letterIndexWrapper,
  rootContainer,
} from './Glossary.styles'
import { GlossaryTermItem } from './GlossaryTermItem'

const GlossaryContent = () => {
  const theme = useTheme()
  const location = useLocation()

  const { data: terms, isLoading: isTermsLoading, error: termsError } = useFetchGlossaryTerms()

  const groupedGlossary = useMemo(() => groupGlossaryByLetter(terms ?? []), [terms])
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const termRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const [activeLetter, setActiveLetter] = useState<string>('A')

  const lettersWithDefinitions = useMemo(() => {
    return alphabet
      .filter((l) => Boolean(groupedGlossary?.[l]?.length))
      .map((l) => [l, groupedGlossary[l]] as const)
  }, [groupedGlossary])

  const scrollToLetter = useCallback((letter: string) => {
    setActiveLetter(letter)
    sectionRefs.current[letter]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const scrollToTerm = useCallback(
    (slug: string) => {
      const termElement = termRefs.current[slug]
      if (termElement) {
        const term = terms?.find((t) => t.slug === slug)
        if (term) {
          setActiveLetter(term.letter.toUpperCase())
        }

        termElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

        termElement.style.transition = 'background-color 0.3s ease'
        termElement.style.backgroundColor = theme.palette.action.hover

        setTimeout(() => {
          termElement.style.backgroundColor = 'transparent'
        }, 2000)
      }
    },
    [terms, theme],
  )

  useEffect(() => {
    const hash = location.hash.slice(1) // remove the '#' from the hash
    if (hash && terms) {
      setTimeout(() => {
        scrollToTerm(hash)
      }, 100)
    }
  }, [location.hash, terms, scrollToTerm])

  if (isTermsLoading) {
    return (
      <CenteredBox>
        <CircularProgress />
      </CenteredBox>
    )
  }

  if (termsError) {
    return <CenteredBox>Error loading glossary</CenteredBox>
  }

  return (
    <Box css={rootContainer}>
      {/* Letter index */}
      <Box css={letterIndexWrapper}>
        <Box css={letterIndex(theme)}>
          {alphabet.map((letter) => {
            const enabled = Boolean(groupedGlossary?.[letter]?.length)

            return (
              <Box
                key={letter}
                onClick={() => {
                  if (!enabled) return
                  scrollToLetter(letter)
                }}
                className={enabled ? (activeLetter === letter ? 'selected' : '') : 'disabled'}
                role="button"
                aria-label={`Scroll to letter ${letter}`}
                tabIndex={enabled ? 0 : -1}
                aria-disabled={!enabled}
                onKeyDown={(e) => {
                  if (!enabled) return
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    scrollToLetter(letter)
                  }
                }}
              >
                {letter}
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Content */}
      <Box css={glossaryContentsOuter}>
        <Box css={glossaryContents(theme)}>
          {lettersWithDefinitions.map(([letter, definitions]) => (
            <Box
              key={letter}
              data-letter={letter}
              ref={(el) => {
                sectionRefs.current[letter] = el as HTMLDivElement | null
              }}
            >
              <h1>{letter}</h1>

              {definitions.map((def) => (
                <GlossaryTermItem
                  key={def.documentId ?? def.term}
                  def={def}
                  onRegisterRef={(slug, el) => {
                    termRefs.current[slug] = el
                  }}
                />
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default GlossaryContent
